import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
    Box,
    Button,
    Checkbox,
    Container,
    Divider,
    FormControlLabel,
    IconButton,
    Typography,
    CircularProgress,
} from '@mui/material';
import { useGetConfigurations } from '../api/NewsQueueApi';
import { useLocalStorageState } from '../newsQueue/index.jsx';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
    NEWS_QUEUE_TITLE
} from '../newsQueue/NewsQueueConstants';

// Default shorthands will be created dynamically from available media
const createDefaultShorthands = (availableMedia) => {
    // If we have at least 2 media, create reasonable shorthands
    if (availableMedia.length >= 2) {
        return {
            politics: [availableMedia[0], availableMedia[1]],
            sports: [availableMedia[0], availableMedia[1]],
            local: [availableMedia[0], availableMedia[1]]
        };
    }
    // If only one media, use it for all shorthands
    return {
        politics: availableMedia,
        sports: availableMedia,
        local: availableMedia
    };
};

export default function MediaSelectionPage() {
    const intl = useIntl();
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const configurationsQuery = useGetConfigurations({ enabled: true });
    const [selectedMedia, setSelectedMedia] = useLocalStorageState('newsQueue.selectedMedia', null);
    const [, setSelectedTargetMedias] = useLocalStorageState('newsQueue.selectedTargetMedias', []);
    const [tempSelectedMedia, setTempSelectedMedia] = useState([]);

    const title = intl.formatMessage({ id: 'newsQueue.mediaSelection.title' });
    const description = intl.formatMessage({ id: 'newsQueue.mediaSelection.description' });
    const saveButton = intl.formatMessage({ id: 'newsQueue.mediaSelection.save' });
    const shortcuts = intl.formatMessage({ id: 'newsQueue.mediaSelection.shortcuts' });

    const availableMedia = useMemo(() => {
        const cfg = configurationsQuery?.data?.result;
        if (!cfg) return [];

        // Get media IDs from the media_name_by_target_media structure
        const configured = cfg?.media_name_by_target_media && typeof cfg.media_name_by_target_media === 'object'
            ? Object.keys(cfg.media_name_by_target_media)
                .filter((x) => typeof x === 'string')
                .map((x) => x.trim())
                .filter((x) => x.length > 0)
            : [];

        return configured;
    }, [configurationsQuery?.data?.result]);

    // Create mapping from media ID to URL for external links
    // For media section: prefer collections where media is alone (single media collections)
    const mediaUrlMap = useMemo(() => {
        const cfg = configurationsQuery?.data?.result;
        const map = {};
        
        if (cfg?.media_collections && Array.isArray(cfg.media_collections)) {
            // First pass: find collections with single media (explicit definitions)
            cfg.media_collections.forEach(collection => {
                if (collection.target_medias && Array.isArray(collection.target_medias) && 
                    collection.target_medias.length === 1) {
                    const mediaId = collection.target_medias[0];
                    // Only map if not already set (single media collections have priority)
                    if (!map[mediaId]) {
                        map[mediaId] = collection.url;
                    }
                }
            });
            
            // Second pass: fill in remaining media from larger collections
            cfg.media_collections.forEach(collection => {
                if (collection.target_medias && Array.isArray(collection.target_medias) && 
                    collection.target_medias.length > 1) {
                    collection.target_medias.forEach(mediaId => {
                        // Only map if not already set by single media collection
                        if (!map[mediaId]) {
                            map[mediaId] = collection.url;
                        }
                    });
                }
            });
        }
        
        return map;
    }, [configurationsQuery?.data?.result]);

    useEffect(() => {
        if (selectedMedia) {
            setTempSelectedMedia(selectedMedia);
        }
    }, [selectedMedia]);

    const handleMediaToggle = (mediaId) => {
        setTempSelectedMedia((prev) =>
            prev.includes(mediaId)
                ? prev.filter((id) => id !== mediaId)
                : [...prev, mediaId]
        );
    };

    const handleSave = () => {
        // Save media selection to localStorage
        try {
            localStorage.setItem('newsQueue.selectedMedia', JSON.stringify(tempSelectedMedia));
            // Explicitly clear the media filter in localStorage
            localStorage.setItem('newsQueue.selectedTargetMedias', JSON.stringify([]));
        } catch {
            // Ignore quota errors
        }
        setSelectedMedia(tempSelectedMedia);
        // Clear the media filter state to show all pre-filtered media
        setSelectedTargetMedias([]);
        navigate('/');
    };

    const shorthands = configurationsQuery?.data?.result?.desk_shortcuts || 
                      createDefaultShorthands(availableMedia);

    const handleShortcut = (shortcutKey) => {
        setTempSelectedMedia(shorthands[shortcutKey] || []);
    };

    // Group media by first letter
    const groupedMedia = useMemo(() => {
        const groups = {};
        availableMedia.forEach(mediaId => {
            const displayName = configurationsQuery?.data?.result?.media_name_by_target_media?.[mediaId] || mediaId;
            const firstLetter = displayName.charAt(0).toUpperCase();
            if (!groups[firstLetter]) {
                groups[firstLetter] = [];
            }
            groups[firstLetter].push({ id: mediaId, displayName });
        });
        
        // Sort media alphabetically within each letter group
        Object.keys(groups).forEach(letter => {
            groups[letter].sort((a, b) => a.displayName.localeCompare(b.displayName, 'fi-FI', { sensitivity: 'base' }));
        });
        
        return groups;
    }, [availableMedia, configurationsQuery?.data?.result?.media_name_by_target_media]);

    // Prepare sorted groups for display
    const sortedGroups = useMemo(() => {
        // Convert groups to array and sort alphabetically
        const groupEntries = Object.entries(groupedMedia);
        groupEntries.sort(([letterA], [letterB]) => letterA.localeCompare(letterB, 'fi-FI', { sensitivity: 'base' }));
        return groupEntries;
    }, [groupedMedia]);

    if (configurationsQuery.isLoading) {
        return (
            <Container maxWidth="md" sx={{ py: 4, textAlign: 'center' }}>
                <CircularProgress />
            </Container>
        );
    }

    if (configurationsQuery.isError) {
        return (
            <Container maxWidth="md" sx={{ py: 4 }}>
                <Typography variant="h6" color="error">
                    {intl.formatMessage({ id: 'error.configuration' })}
                </Typography>
            </Container>
        );
    }

    if (availableMedia.length === 0) {
        return (
            <Container maxWidth="md" sx={{ py: 4 }}>
                <Typography variant="h6" color="error">
                    {intl.formatMessage({ id: 'monitoring.empty' })}
                </Typography>
            </Container>
        );
    }

    return (
        <Container maxWidth="md" sx={{ 
            py: 4, 
            display: 'flex', 
            flexDirection: 'column',
            height: '100vh',
            maxHeight: '100vh'
        }}>
            {/* Title area - fixed height */}
            <Box sx={{ 
                textAlign: 'center', 
                mb: 2, 
                flexShrink: 0 
            }}>
                <Typography
                    variant="h3"
                    component="h1"
                    sx={{ 
                        fontWeight: 700, 
                        fontFamily: 'monospace',
                        color: 'primary.main', }}
                    gutterBottom
                >
                    {NEWS_QUEUE_TITLE}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    {title}
                </Typography>
            </Box>

            <Divider />
            {/* Media selector - scrollable area */}
            <Box sx={{ 
                mb: 4, 
                pt: 2,
                flexGrow: 1,
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column'
            }}>
                <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    gap: 2,
                    flexGrow: 1
                }}>
                <Typography variant="body1" color="text.secondary">
                    {description}
                </Typography>

                {/* Media Collections Quick Links */}
                {configurationsQuery?.data?.result?.media_collections && (
                    <Box sx={{ mt: 3, mb: 2, textAlign: 'left' }}>
                        <Typography variant="h6" sx={{ 
                            fontWeight: 'bold',
                            mb: 1,
                            color: 'primary.main',
                            textAlign: 'left'
                        }}>
                            {intl.formatMessage({ id: 'newsQueue.mediaSelection.collections' })}
                        </Typography>
                        <Box sx={{ 
                            display: 'flex', 
                            flexDirection: 'column',
                            gap: 1,
                            pt: 1,
                            pb: 1,
                            maxWidth: '280px',
                            borderBottom: '1px solid',
                            borderColor: 'rgba(0, 0, 0, 0.12)',
                        }}>
                            {configurationsQuery.data.result.media_collections
                                .filter(collection => 
                                    Array.isArray(collection.target_medias) && 
                                    collection.target_medias.length > 1
                                )
                                .map((collection) => (
                                    <Box
                                        key={collection.url}
                                        sx={{ 
                                            display: 'flex', 
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            pt: 0.5,
                                            pb: 0.5,
                                            '&:hover': {
                                                backgroundColor: 'action.hover',
                                                cursor: 'pointer'
                                            }
                                        }}
                                        onClick={() => navigate(`/desk/${collection.url}`)}
                                    >
                                        <Typography variant="body1" sx={{ flexGrow: 1 }}>
                                            {collection.name}
                                        </Typography>
                                        <IconButton
                                            size="small"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                navigate(`/desk/${collection.url}`);
                                            }}
                                            color="primary"
                                            aria-label={`Avaa ${collection.name}`}
                                        >
                                            <OpenInNewIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                ))}
                        </Box>
                    </Box>
                )
                }

                {/* Mediat subtitle */}
                <Typography variant="h6" sx={{ 
                    fontWeight: 'bold',
                    mt: 2,
                    mb: 1,
                    color: 'primary.main',
                    textAlign: 'left'
                }}>
                    {intl.formatMessage({ id: 'newsQueue.mediaSelection.media' })}
                </Typography>

                {/* Display groups - all groups on mobile, 2 per row on desktop */}
                    {isMobile ? (
                        // Mobile: single column with all groups
                        sortedGroups.map(([letter, mediaItems], index) => (
                            <React.Fragment key={letter}>
                                {index > 0 && <Divider sx={{ my: 2 }} />}
                                <Box sx={{ 
                                    flex: 1, 
                                    display: 'flex', 
                                    flexDirection: 'column', 
                                    gap: 1,
                                    px: 2
                                }}>
                                    <Typography variant="subtitle1" sx={{ 
                                        fontWeight: 'bold', 
                                        mt: 2, 
                                        mb: 0,
                                        textAlign: 'left',
                                        color: 'primary.main'
                                    }}>
                                        {letter}
                                    </Typography>
                                    {mediaItems.map((media) => (
                                        <Box key={media.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={tempSelectedMedia.includes(media.id)}
                                                        onChange={() => handleMediaToggle(media.id)}
                                                    />
                                                }
                                                label={media.displayName}
                                                sx={{ flexGrow: 1 }}
                                            />
                                            {mediaUrlMap[media.id] && (
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        navigate(`/media/${mediaUrlMap[media.id]}`);
                                                    }}
                                                    color="primary"
                                                    aria-label={`Avaa ${media.displayName}`}
                                                >
                                                    <OpenInNewIcon fontSize="small" />
                                                </IconButton>
                                            )}
                                        </Box>
                                    ))}
                                </Box>
                            </React.Fragment>
                        ))
                    ) : (
                        // Desktop: 2 groups per row
                        Array.from({ length: Math.ceil(sortedGroups.length / 2) }).map((_, rowIndex) => {
                            const leftGroupIndex = rowIndex * 2;
                            const rightGroupIndex = rowIndex * 2 + 1;
                            const leftGroup = sortedGroups[leftGroupIndex];
                            const rightGroup = sortedGroups[rightGroupIndex];
                            
                            return (
                                <React.Fragment key={rowIndex}>
                                    {rowIndex > 0 && false && <Divider sx={{ my: 2 }} />}
                                    <Box sx={{ 
                                        display: 'flex', 
                                        gap: 4,
                                        flexWrap: 'nowrap'
                                    }}>
                                        {/* Left group */}
                                        {leftGroup && (
                                            <Box sx={{ 
                                                flex: 1, 
                                                display: 'flex', 
                                                flexDirection: 'column', 
                                                gap: 1,
                                                //minWidth: '300px'
                                            }}>
                                                <Typography variant="subtitle1" sx={{ 
                                                    fontWeight: 'bold', 
                                                    mt: 2, 
                                                    mb: 0,
                                                    textAlign: 'left',
                                                    color: 'primary.main'
                                                }}>
                                                    {leftGroup[0]}
                                                </Typography>
                                                {leftGroup[1].map((media) => (
                                                    <Box key={media.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                        <FormControlLabel
                                                            control={
                                                                <Checkbox
                                                                    checked={tempSelectedMedia.includes(media.id)}
                                                                    onChange={() => handleMediaToggle(media.id)}
                                                                />
                                                            }
                                                            label={media.displayName}
                                                            sx={{ flexGrow: 1 }}
                                                        />
                                                        {mediaUrlMap[media.id] && (
                                                            <IconButton
                                                                size="small"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    navigate(`/media/${mediaUrlMap[media.id]}`);
                                                                }}
                                                                color="primary"
                                                                aria-label={`Avaa ${media.displayName}`}
                                                            >
                                                                <OpenInNewIcon fontSize="small" />
                                                            </IconButton>
                                                        )}
                                                    </Box>
                                                ))}
                                                {<Divider sx={{ my: 2 }} />}
                                            </Box>
                                        )}
                                        
                                        {/* Right group */}
                                        {rightGroup && (
                                            <Box sx={{ 
                                                flex: 1, 
                                                display: 'flex', 
                                                flexDirection: 'column', 
                                                gap: 1,
                                                minWidth: '300px',
                                                px: 4
                                            }}>
                                                <Typography variant="subtitle1" sx={{ 
                                                    fontWeight: 'bold', 
                                                    mt: 2, 
                                                    mb: 0,
                                                    textAlign: 'left',
                                                    color: 'primary.main'
                                                }}>
                                                    {rightGroup[0]}
                                                </Typography>
                                                {rightGroup[1].map((media) => (
                                                    <Box key={media.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                        <FormControlLabel
                                                            control={
                                                                <Checkbox
                                                                    checked={tempSelectedMedia.includes(media.id)}
                                                                    onChange={() => handleMediaToggle(media.id)}
                                                                />
                                                            }
                                                            label={media.displayName}
                                                            sx={{ flexGrow: 1 }}
                                                        />
                                                        {mediaUrlMap[media.id] && (
                                                            <IconButton
                                                                size="small"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    navigate(`/media/${mediaUrlMap[media.id]}`);
                                                                }}
                                                                color="primary"
                                                                aria-label={`Avaa ${media.displayName}`}
                                                            >
                                                                <OpenInNewIcon fontSize="small" />
                                                            </IconButton>
                                                        )}
                                                    </Box>
                                                ))}
                                                {<Divider sx={{ my: 2 }} />}
                                            </Box>
                                        )}
                                    </Box>
                                </React.Fragment>
                            );
                        })
                    )}
                </Box>
            </Box>

            {
                false && (
                    <>
                        <Divider sx={{ my: 4 }} />

                        <Box sx={{ mb: 4 }}>
                            <Typography variant="h6" gutterBottom>
                                {shortcuts}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                {Object.entries(shorthands).map(([key, mediaIds]) => (
                                    <Button
                                        key={key}
                                        variant="outlined"
                                        onClick={() => handleShortcut(key)}
                                        sx={{ textTransform: 'none' }}
                                    >
                                        {intl.formatMessage({ id: `newsQueue.mediaSelection.shortcut.${key}` })}
                                    </Button>
                                ))}
                            </Box>
                        </Box>
                    </> 
                )
            }

            {/* Save button - fixed at bottom */}
            <Box sx={{ 
                display: 'flex', 
                justifyContent: 'flex-end',
                flexShrink: 0,
                mt: 'auto'
            }}>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSave}
                    disabled={tempSelectedMedia.length === 0}
                >
                    {saveButton}
                </Button>
            </Box>
            
        </Container>
    );
}
